import partNumberSchemas from "../info/partNumberSchemas";

export function getSchemaById(schemaId) {
  return partNumberSchemas.find(schema => schema.id === schemaId) || null;
}

export function getSchemaPosition(schema, label) {
  if (!schema) return null;

  return schema.positions?.find(position => position.label === label) || null;
}

export function getPositionOptions(schemaId, label) {
  const schema = getSchemaById(schemaId);
  const position = getSchemaPosition(schema, label);

  if (!position || !Array.isArray(position.options)) return [];

  return position.options;
}

export function getGroupedOption(schemaId, groupName) {
  const schema = getSchemaById(schemaId);

  if (!schema) return null;

  return schema.groupedOptions?.find(group => group.group === groupName) || null;
}

export function getGroupedOptions(schemaId, groupName) {
  const group = getGroupedOption(schemaId, groupName);

  return Array.isArray(group?.options) ? group.options : [];
}

export function getKnownPartNumbers(schemaId) {
  const schema = getSchemaById(schemaId);

  return schema?.knownPartNumbers || [];
}

export function getSlugFromProductPage(productPage) {
  if (!productPage) return "";

  return productPage
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/products\//, "")
    .replace(/^\//, "")
    .replace(/\/$/, "")
    .trim();
}

export function buildPartNumber(schemaId, config = {}) {
  const schema = getSchemaById(schemaId);

  if (!schema) {
    return {
      valid:false,
      code:"",
      error:`Schema not found: ${schemaId}`,
    };
  }

  if (schema.lookupOnly) {
    const requestedKnownPart = config.knownPartNumber;

    if (!requestedKnownPart) {
      return {
        valid:false,
        code:"",
        error:"This product uses known part numbers rather than generated configuration.",
      };
    }

    const match = schema.knownPartNumbers?.find(
      item => item.code.toUpperCase() === requestedKnownPart.toUpperCase()
    );

    return match
      ? {
          valid:true,
          code:match.code,
          schema,
          description:match.description,
        }
      : {
          valid:false,
          code:"",
          schema,
          error:`Unknown part number: ${requestedKnownPart}`,
        };
  }

  const chars = Array.from({ length:schema.maxLength }, () => "");

  // Automatically fill positions which only have one possible code.
  schema.positions?.forEach(position => {
    if (Array.isArray(position.options) && position.options.length === 1) {
      chars[position.index] = position.options[0].code;
    }
  });

  // Fill normal one-character fields using the schema label.
  Object.entries(config.positions || {}).forEach(([label, code]) => {
    const position = schema.positions?.find(item => item.label === label);

    if (!position) return;

    chars[position.index] = String(code).toUpperCase();
  });

  // Fill grouped fields across however many indexes that schema defines.
  Object.entries(config.groups || {}).forEach(([groupName, value]) => {
    const group = schema.groupedOptions?.find(item => item.group === groupName);

    if (!group) return;

    const pieces = String(value).toUpperCase().split("");

    group.indexes.forEach((index, i) => {
      chars[index] = pieces[i] || "";
    });
  });

  // Remove unused optional trailing positions.
  while (chars.length > schema.minLength && !chars[chars.length - 1]) {
    chars.pop();
  }

  const code = chars.join("");

  const requiredMissing = schema.positions?.some(position => {
    if (!position.required) return false;
    return !chars[position.index];
  });

  if (requiredMissing) {
    return {
      valid:false,
      code,
      schema,
      error:"Required part-number fields are incomplete.",
    };
  }

  return {
    valid:true,
    code,
    schema,
  };
}